import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Sparkles, ClipboardList, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatEt } from "@/lib/eastern";
import { Logo } from "@/components/Logo";

const OPEN_PREFIXES = ["/admin", "/publish", "/survey", "/forgot-password", "/reset-password", "/privacy"];

export function LaunchGate({ children }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [status, setStatus] = useState(null);
  const [offset, setOffset] = useState(0);

  const check = () => api.get("/launch").then((r) => { setStatus(r.data); setOffset(new Date(r.data.now).getTime() - Date.now()); }).catch(() => setStatus({ gated: false }));
  useEffect(() => { check(); }, []);

  const bypass = OPEN_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  if (!status) return bypass ? children : null;
  if (!status.gated) return children;
  if (bypass) return children;
  if (user === null) return null;
  if (user) return <><PreviewBanner launchAt={status.launch_at} />{children}</>;
  return <ComingSoon launchAt={status.launch_at} offset={offset} onReached={check} />;
}

const PreviewBanner = ({ launchAt }) => (
  <div className="sticky top-0 z-[60] bg-amber-400 text-[#070F1E] text-xs sm:text-sm font-semibold text-center px-4 py-2" data-testid="launch-preview-banner">
    <Lock size={13} className="inline -mt-0.5 mr-1.5" />Launch gate is on — you're previewing as a team member. Visitors see the countdown{launchAt ? ` until ${formatEt(launchAt)}` : ""}. <Link to="/admin/settings" className="underline ml-1">Change</Link>
  </div>
);

const pad = (n) => String(Math.max(0, n)).padStart(2, "0");

function ComingSoon({ launchAt, offset, onReached }) {
  const [left, setLeft] = useState(() => remaining(launchAt, offset));
  useEffect(() => {
    const id = setInterval(() => {
      const r = remaining(launchAt, offset);
      setLeft(r);
      if (launchAt && r.total <= 0) { clearInterval(id); onReached(); }
    }, 1000);
    return () => clearInterval(id);
  }, [launchAt, offset, onReached]);
  const units = [["Days", left.d], ["Hours", left.h], ["Minutes", left.m], ["Seconds", left.s]];
  return (
    <div className="min-h-screen bg-[#050B17] text-white flex flex-col grain relative overflow-hidden" data-testid="launch-gate">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 20% 0%, rgba(56,189,248,0.18), transparent 55%), radial-gradient(ellipse at 90% 100%, rgba(5,54,113,0.6), transparent 60%)" }} />
      <main className="relative flex-1 flex items-center">
        <div className="max-w-[1400px] w-full mx-auto px-6 sm:px-10 py-16 grid lg:grid-cols-[1.1fr_1fr] gap-14 items-center">
          <div className="reveal">
            <Logo size={72} />
            <p className="eyebrow mt-10 flex items-center gap-2"><Sparkles size={12} /> Launching soon</p>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02]" data-testid="launch-gate-title">Underdog Podcasts<br />launches at <span className="text-sky-400">{launchAt ? formatEt(launchAt) : "a time to be announced"}</span></h1>
            <p className="mt-6 text-base md:text-lg text-slate-300 max-w-xl">The home of the Underdog Podcast Network — every show, every host, every episode in one place. We're putting the finishing touches on it now.</p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/survey" className="inline-flex items-center gap-2 rounded-full bg-sky-400 px-6 py-3 text-sm font-bold text-[#070F1E] hover:bg-sky-300 transition-colors" data-testid="launch-gate-survey-link"><ClipboardList size={16} /> Take the listener survey</Link>
              <span className="text-xs text-slate-400">About 5 minutes · chance to win $100</span>
            </div>
          </div>
          <div className="reveal" style={{ animationDelay: "120ms" }}>
            <div className="card-surface rounded-3xl p-8 sm:p-10 shadow-2xl shadow-black/50">
              <p className="eyebrow">Countdown</p>
              <div className="mt-6 grid grid-cols-4 gap-3 sm:gap-4" data-testid="launch-gate-countdown">
                {units.map(([label, v]) => (
                  <div key={label} className="rounded-2xl bg-white/5 border border-white/10 py-5 text-center">
                    <p className="font-display text-3xl sm:text-5xl font-black tabular-nums" data-testid={`launch-gate-${label.toLowerCase()}`}>{pad(v)}</p>
                    <p className="mt-1 text-[10px] sm:text-xs font-mono uppercase tracking-widest text-slate-400">{label}</p>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-xs text-slate-500">{launchAt ? "Doors open automatically — no refresh needed." : "Check back soon."}</p>
            </div>
          </div>
        </div>
      </main>
      <footer className="relative px-6 sm:px-10 py-6 flex items-center justify-between text-xs text-slate-500">
        <span>© {new Date().getFullYear()} Underdog Podcasts</span>
        <Link to="/admin/login" className="hover:text-slate-300" data-testid="launch-gate-team-login">Team sign in</Link>
      </footer>
    </div>
  );
}

function remaining(launchAt, offset) {
  const total = launchAt ? new Date(launchAt).getTime() - (Date.now() + offset) : 0;
  const sec = Math.max(0, Math.floor(total / 1000));
  return { total, d: Math.floor(sec / 86400), h: Math.floor((sec % 86400) / 3600), m: Math.floor((sec % 3600) / 60), s: sec % 60 };
}
